import type { Session } from '@supabase/supabase-js';
import * as React from 'react';

import { supabase } from '@sixam/supabase/client';
import type { Database } from '@sixam/shared/database.types';

type Profile = Database['public']['Tables']['profiles']['Row'];

type SessionContextValue = {
  session: Session | null;
  profile: Profile | null;
  isAdmin: boolean;
  isLoading: boolean;
  isProfileLoading: boolean;
  signOut: () => Promise<void>;
};

const SessionContext = React.createContext<SessionContextValue | undefined>(undefined);

export function SessionProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [session, setSession] = React.useState<Session | null>(null);
  const [profile, setProfile] = React.useState<Profile | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isProfileLoading, setIsProfileLoading] = React.useState(false);

  React.useEffect(() => {
    supabase.auth
      .getSession()
      .then(({ data }) => setSession(data.session))
      .finally(() => setIsLoading(false));

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });

    return () => data.subscription.unsubscribe();
  }, []);

  const userId = session?.user.id;

  React.useEffect(() => {
    let active = true;

    async function loadProfile() {
      if (!userId) {
        if (active) {
          setProfile(null);
          setIsProfileLoading(false);
        }
        return;
      }

      setIsProfileLoading(true);
      const { data } = await supabase.from('profiles').select('*').eq('id', userId).single();
      if (active) {
        setProfile(data);
        setIsProfileLoading(false);
      }
    }

    void loadProfile();

    return () => {
      active = false;
    };
  }, [userId]);

  const value = React.useMemo<SessionContextValue>(
    () => ({
      session,
      profile,
      isAdmin: profile?.role === 'admin',
      isLoading,
      isProfileLoading,
      signOut: async () => {
        await supabase.auth.signOut();
      },
    }),
    [session, profile, isLoading, isProfileLoading]
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}

export function useSession() {
  const context = React.useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}
